import { ScanResponse } from '@/models/Scans'
import {
  ListItem,
  Button,
  Divider,
  Alert,
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  CircularProgress,
  IconButton,
  LinearProgress,
  Chip,
} from '@mui/material'
import { getOpportunityDisplayInfo } from '@/utils/dateUtils'
import UpgradeCTA from './UpgradeCTA'
import ThumbDownIcon from '@mui/icons-material/ThumbDown'
import ThumbUpIcon from '@mui/icons-material/ThumbUp'
import SavedSearchIcon from '@mui/icons-material/SavedSearch'
import { AccessibleTooltip } from './AccessibleTooltip'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import StarOutlineIcon from '@mui/icons-material/StarOutline'
import GradeIcon from '@mui/icons-material/Grade'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'

import ReactMarkdown from 'react-markdown'
import { useEffect, useState } from 'react'

import { getCurrentUser } from '@/utils/auth/UserProvider'
import { UUID } from 'crypto'
import { Link } from 'react-router-dom'
import Loading from './Loading'
import { useQueryClient } from '@tanstack/react-query'
import FeedbackNegativeModal from './FeedbackNegativeModal'
import FeedbackPostiveModal from './FeedbackPostiveModal'
import { useApplicationTask } from '@/utils/tasks/applicationTask'
import { useProfileRegenGuard } from '@/components/ProfileRegenBlockProvider'
import {
  addRemoveFavourite,
  fetchLatestRelationalIntroductionLetter,
} from '@/api/scansAPI'
import GenerateRelationalIntroductionLetter from './GenerateRelationalIntroductionLetter'
import React from 'react'
import { ApplicationStatus } from '@/models/Applications'

function renderTextWithLinks(text: string) {
  // match URLs
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g
  const parts = text.split(urlRegex)

  return parts.map((part, index) => {
    if (part.match(urlRegex)) {
      // remove trailing punctuation
      const trailingPunctuationMatch = part.match(/([.,;:!?]+)$/)
      const trailingPunctuation = trailingPunctuationMatch
        ? trailingPunctuationMatch[0]
        : ''
      const cleanUrl = trailingPunctuation
        ? part.slice(0, -trailingPunctuation.length)
        : part

      // add https if missing
      const href = cleanUrl.startsWith('www.')
        ? `https://${cleanUrl}`
        : cleanUrl

      return (
        <React.Fragment key={index}>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'inherit', textDecoration: 'underline' }}
          >
            {part}
          </a>
        </React.Fragment>
      )
    }
    return part
  })
}

function convertUrlsToMarkdown(text: string) {
  // match URLs that are NOT already inside markdown links or autolinks
  // ensures URL doesn't follow (, [, or <
  const urlRegex = /(?<![([<])(https?:\/\/[^\s)]+|www\.[^\s)]+)(?![\]>)])/g
  return text.replace(urlRegex, (match) => {
    // remove trailing punctuation
    const trailingPunctuationMatch = match.match(/([.,;:!?]+)$/)
    const trailingPunctuation = trailingPunctuationMatch
      ? trailingPunctuationMatch[0]
      : ''
    const cleanUrl = trailingPunctuation
      ? match.slice(0, -trailingPunctuation.length)
      : match

    const href = cleanUrl.startsWith('www.') ? `https://${cleanUrl}` : cleanUrl
    return `[${cleanUrl}](${href})${trailingPunctuation}`
  })
}

interface MatchNeedsListItemProps {
  result: ScanResponse
  isFuturePlanning?: boolean
  orgUuid?: UUID
  needId?: number
  token?: string
  existingApplication?: { id: number; status?: ApplicationStatus } | null
  applicationsLoading?: boolean
  draftRestriction?: 'upgrade' | 'not_open'
}

function getApplicationBorderColor(status?: ApplicationStatus) {
  switch (status) {
    case 'submitted':
      return '#D4A017'
    case 'won':
      return '#2E7D32'
    case 'lost':
      return '#C62828'
    default:
      return '#1C3045'
  }
}

export default function MatchResultsListItem({
  result,
  isFuturePlanning = false,
  token: passedToken,
  existingApplication,
  applicationsLoading = false,
  draftRestriction,
}: MatchNeedsListItemProps) {
  const [showResultDetails, setShowResultDetails] = useState(false)
  const [showOppDetails, setShowOppDetails] = useState(false)
  const [applicationId, setApplicationId] = useState<number | null>(null)

  const [generating, setGenerating] = useState(false)
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false)
  const [upgradeReason, setUpgradeReason] = useState<string>('')
  const [showNegativeFeedbackModal, setShowNegativeFeedbackModal] =
    useState(false)
  const [showPositiveFeedbackModal, setShowPositiveFeedbackModal] =
    useState(false)
  const [isFavorite, setIsFavorite] = useState<boolean>(result.favourite)
  const [favouritingError, setFavouritingError] = useState<boolean>(false)
  const [showIntroductionLetterModal, setShowIntroductionLetterModal] =
    useState(false)
  const [introLetterState, setIntroLetterState] = useState<
    'none' | 'generating' | 'draft' | 'done'
  >('none')

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const applicationBorderColor = getApplicationBorderColor(
    existingApplication?.status,
  )

  const queryClient = useQueryClient()

  const { token: currentUserToken, isLoading } = getCurrentUser()
  const token = passedToken || currentUserToken
  const guardRegen = useProfileRegenGuard()

  const {
    startApplicationGeneration,
    hasActiveApplicationGeneration,
    isLoadingForScanId,
    errorForScanId,
    progressForScanId,
    statusForScanId,
    isApplicationGenerationStaleForScan,
  } = useApplicationTask({
    token: token as string,
    onCompleted: () => {
      // Query invalidation is handled directly in TaskContext
      setGenerating(false)
    },
    onError: (error) => {
      // Reset starting state
      setGenerating(false)
      if (guardRegen(error)) return
      if (error.name === 'PaymentRequired') {
        // Show upgrade dialog with the specific error message
        setUpgradeReason(error.message)
        setShowUpgradeDialog(true)
      }
      // other errors handled by the task system
    },
  })

  // Application state is managed by the parent component
  // No need to refresh on individual component mount

  // Handle feedback submission
  const handleFeedbackSubmitted = () => {
    // Refresh the scan results to hide this match
    queryClient.invalidateQueries({
      queryKey: ['scanResultsByNeedId', result.need_id],
    })
  }

  useEffect(() => {
    if (existingApplication) {
      setApplicationId(existingApplication.id)
    } else {
      setApplicationId(null)
    }
  }, [existingApplication])

  function handleShowResultDetails() {
    setShowResultDetails(!showResultDetails)
    if (showOppDetails) {
      setShowOppDetails(false)
    }
  }

  function handleShowOppDetails() {
    setShowOppDetails(!showOppDetails)
    if (showResultDetails) {
      setShowResultDetails(false)
    }
  }

  // Get UI state from the task hook
  const scanId = result.id ? String(result.id) : ''

  // Check if scan is stale
  const isStale = scanId ? isApplicationGenerationStaleForScan(scanId) : false

  // Is actively scanning (not stale)
  const isGeneratingApplication = scanId
    ? (hasActiveApplicationGeneration(scanId) ||
        isLoadingForScanId(scanId) ||
        generating) &&
      !isStale
    : false

  const generationError = scanId ? errorForScanId(scanId) : null

  // Application generation progress info
  const applicationProgress = scanId
    ? {
        progress: progressForScanId(scanId) || 0,
        message: isStale
          ? 'Waiting...'
          : statusForScanId(scanId) === 'running'
            ? 'Generating...'
            : generating
              ? 'Starting...'
              : 'Initializing...',
      }
    : { progress: 0, message: '' }

  // Get opportunity display info (status and message) once
  const displayInfo = getOpportunityDisplayInfo(
    result.opportunity,
    result.application_open_close_dates,
  )

  async function handleGenerateApplication(scanId: number) {
    if (!token) return
    if (!scanId || isNaN(scanId)) return

    if (draftRestriction === 'upgrade') {
      setUpgradeReason('You need to upgrade to write more applications.')
      setShowUpgradeDialog(true)
      return
    }

    try {
      setGenerating(true)
      const jobId = await startApplicationGeneration(
        String(scanId),
        result.need_id,
        result.org_uuid,
      )
      if (!jobId) {
        // If no job ID returned but no error thrown, must be already scanning
        setGenerating(false)
      }
    } catch (error) {
      setGenerating(false)
      if (error instanceof Error && error.name === 'PaymentRequired') {
        // Show upgrade dialog with the specific error message
        setUpgradeReason(error.message)
        setShowUpgradeDialog(true)
      } else {
        // Error is handled by the task system, no need to set local error state
      }
    }
  }

  async function handleToggleFavourite(currentMark: boolean) {
    if (!token) return
    try {
      await addRemoveFavourite(token, result.id, currentMark)

      setIsFavorite(!currentMark)
    } catch (error) {
      setFavouritingError(true)
    }
  }

  useEffect(() => {
    const loadLetterState = async () => {
      if (!token || !result.opportunity.is_relational || !result.id) return
      try {
        const latest = await fetchLatestRelationalIntroductionLetter(
          token,
          result.id,
        )
        if (!latest) {
          setIntroLetterState('none')
          return
        }
        setIntroLetterState(latest.draft.is_done ? 'done' : 'draft')
      } catch {
        setIntroLetterState('none')
      }
    }

    loadLetterState()
  }, [token, result.id, result.opportunity.is_relational])

  if (isLoading) {
    return <Loading />
  }

  return (
    <>
      {token && (
        <GenerateRelationalIntroductionLetter
          isOpen={showIntroductionLetterModal}
          onClose={() => setShowIntroductionLetterModal(false)}
          token={token as string}
          result={result}
          onDraftStateChange={setIntroLetterState}
        />
      )}

      {/* Upgrade CTA Dialog */}
      <UpgradeCTA
        variant="dialog"
        message={upgradeReason || 'Upgrade required to access this feature'}
        restrictedFeature="Application generation"
        isOpen={showUpgradeDialog}
        onClose={() => setShowUpgradeDialog(false)}
      />

      <ListItem
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          py: result.opportunity.is_relational ? 1 : 3,
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 0 : 4,
          ml: isMobile ? 0 : -2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: isMobile ? 'start' : 'center',
              justifyContent: 'left',
              gap: isMobile ? 0 : 1,
            }}
          >
            {/* Favourite Match Button */}
            {favouritingError ? (
              <AccessibleTooltip
                title={'Failed to toggle favourite, please try again later'}
                placement="bottom"
                arrow
              >
                <span
                  role="img"
                  aria-label="Error: Failed to toggle favourite, please try again later"
                  tabIndex={0}
                >
                  <ErrorOutlineIcon
                    sx={{ fontSize: '18px', color: 'error.light', mx: 1 }}
                  />
                </span>
              </AccessibleTooltip>
            ) : (
              <AccessibleTooltip
                title={
                  isFavorite
                    ? 'Unfavourite'
                    : 'Favourite this match so it appears at the top of the list and receive email updates when its funding rounds open or close'
                }
                placement="bottom"
                arrow
                slotProps={{
                  tooltip: {
                    sx: {
                      textAlign: 'center',
                      width: isFavorite ? '100px' : '180px',
                    },
                  },
                }}
              >
                <IconButton onClick={() => handleToggleFavourite(isFavorite)}>
                  {isFavorite ? (
                    <GradeIcon sx={{ fontSize: '22px', color: '#1C3045' }} />
                  ) : (
                    <StarOutlineIcon
                      sx={{ fontSize: '22px', color: '#1C3045' }}
                    />
                  )}
                </IconButton>
              </AccessibleTooltip>
            )}

            <Typography
              variant="body1"
              fontWeight={
                showResultDetails || showOppDetails || isMobile
                  ? '500'
                  : 'normal'
              }
              sx={{
                color: '#1C3045',
                maxWidth: '400px',
                textAlign: isMobile ? 'center' : 'left',
              }}
              fontSize={isMobile ? '20px' : '16px'}
              mb={isMobile ? 2 : 0}
            >
              {result.opportunity.fund_name
                ? `${result.funder.name} - ${result.opportunity.fund_name}`
                : result.funder.name}
              {/* Status badges */}
              {displayInfo.status === 'closed' &&
                (isFuturePlanning ? (
                  <Chip
                    label="Upcoming dates not confimed"
                    size="small"
                    sx={{
                      ml: 1,
                      height: '20px',
                      fontSize: '11px',
                      border: '1px solid',
                      borderColor: 'warning.main',
                      bgcolor: 'white',
                      color: 'warning.main',
                      fontWeight: 600,
                    }}
                  />
                ) : (
                  <Chip
                    label="Closed"
                    size="small"
                    sx={{
                      ml: 1,
                      height: '20px',
                      fontSize: '11px',
                      bgcolor: 'error.main',
                      color: 'white',
                      fontWeight: 600,
                    }}
                  />
                ))}
              {displayInfo.status === 'closing-soon' && (
                <Chip
                  label="Closing Soon"
                  size="small"
                  sx={{
                    ml: 1,
                    height: '20px',
                    fontSize: '11px',
                    bgcolor: 'warning.main',
                    color: 'white',
                    fontWeight: 600,
                  }}
                />
              )}
            </Typography>
          </Box>

          {/* Admin Notes */}
          {result.notes && (
            <Box
              sx={{
                bgcolor: '#e8f5e8',
                border: '1px solid #c8e6c9',
                borderRadius: 2,
                px: 1.5,
                py: 1,
                display: 'inline-block',
                mb: isMobile ? 2 : 0,
                ml: isMobile ? 0 : 2,
                textAlign: isMobile ? 'center' : 'left',
              }}
            >
              <Typography variant="body2">{result.notes}</Typography>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: isMobile ? 2 : 4,
            alignItems: 'center',
            flexDirection: isMobile ? 'column' : 'row',
            position: 'relative',
          }}
        >
          <Button
            disableRipple
            onClick={() => handleShowResultDetails()}
            aria-expanded={showResultDetails}
            aria-controls={`match-details-${result.id}`}
            sx={{
              textTransform: 'none',
              fontSize: '16px',
              color: '#1C3045',
              borderColor: '#1C3045',
              textDecoration: 'none',
              minWidth: '270px',
              maxHeight: '40px',
              borderRadius: 5,
              border: isMobile ? 2 : 1,
              bgcolor:
                !showOppDetails && showResultDetails
                  ? '#eeeeee'
                  : 'transparent',
            }}
            variant={isMobile ? 'outlined' : 'contained'}
          >
            See how we matched
            <SavedSearchIcon sx={{ ml: 1 }} />
            {!isMobile && !showOppDetails && showResultDetails ? (
              <ExpandLessIcon sx={{ ml: 1 }} />
            ) : (
              <ExpandMoreIcon sx={{ ml: 1 }} />
            )}
          </Button>
          <Button
            disableRipple
            onClick={() => handleShowOppDetails()}
            aria-expanded={showOppDetails}
            aria-controls={`funder-details-${result.id}`}
            sx={{
              textTransform: 'none',
              fontSize: '16px',
              color: '#1C3045',
              borderColor: '#1C3045',
              minWidth: '250px',
              maxHeight: '40px',
              textDecoration:
                showOppDetails && !showResultDetails && !isMobile
                  ? 'underline'
                  : 'none',
              ':hover': {
                textDecoration: isMobile ? 'none' : 'underline',
              },
              border: isMobile ? 2 : 0,
              bgcolor:
                isMobile && showOppDetails && !showResultDetails
                  ? '#eeeeee'
                  : 'transparent',
              '&:focus': {
                backgroundColor: '#e8e8e8',
              },
            }}
            variant={isMobile ? 'outlined' : 'text'}
          >
            {showOppDetails && !showResultDetails ? 'Hide' : 'View'} funder
            information
            {!isMobile && showOppDetails && !showResultDetails ? (
              <ExpandLessIcon sx={{ ml: 1 }} />
            ) : (
              <ExpandMoreIcon sx={{ ml: 1 }} />
            )}
          </Button>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {result.opportunity.is_relational ? (
                <Button
                  variant="contained"
                  disableRipple
                  onClick={() => setShowIntroductionLetterModal(true)}
                  disabled={!token || introLetterState === 'generating'}
                  startIcon={
                    introLetterState === 'generating' ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : undefined
                  }
                  sx={{
                    textTransform: 'none',
                    fontSize: introLetterState === 'none' ? '15px' : '16px',
                    fontWeight: introLetterState === 'none' ? 600 : 400,
                    lineHeight: 'normal',
                    textAlign: 'center',
                    color: introLetterState === 'none' ? 'white' : '#1C3045',
                    bgcolor: introLetterState === 'none' ? '#1C3045' : 'white',
                    border: introLetterState === 'none' ? 0 : 2,
                    borderColor:
                      introLetterState === 'done' ? '#2E7D32' : '#1C3045',
                    width: isMobile ? '100%' : '200px',
                    borderRadius: introLetterState === 'none' ? 2.5 : 2,
                    mt: isMobile ? 2 : 0,
                    px: 2,
                    py: 0,
                    minHeight: '40px',
                    height: '40px',
                    whiteSpace: 'nowrap',
                    boxShadow:
                      introLetterState === 'none'
                        ? '0 8px 16px rgba(28, 48, 69, 0.16)'
                        : '0 4px 10px rgba(28, 48, 69, 0.18)',
                    '&:hover': {
                      bgcolor:
                        introLetterState === 'none' ? '#1C3045' : 'white',
                    },
                    '&.Mui-disabled': {
                      color: introLetterState === 'none' ? 'white' : '#1C3045',
                      bgcolor:
                        introLetterState === 'none' ? '#8FA0AF' : 'white',
                      borderColor:
                        introLetterState === 'done' ? '#2E7D32' : '#1C3045',
                    },
                  }}
                >
                  {introLetterState === 'done'
                    ? 'View introduction letter'
                    : introLetterState === 'draft'
                      ? 'Edit draft'
                      : introLetterState === 'generating'
                        ? 'Drafting...'
                        : 'Draft introduction letter'}
                </Button>
              ) : isFuturePlanning ? (
                <Alert
                  severity="info"
                  icon={false}
                  sx={{ width: '200px', textAlign: 'center' }}
                >
                  Funding Round dates not yet confirmed
                </Alert>
              ) : draftRestriction === 'not_open' ? (
                <Alert
                  severity="info"
                  icon={false}
                  sx={{ width: '200px', textAlign: 'center' }}
                >
                  Check back closer to the opening date to draft an application
                </Alert>
              ) : !result.opportunity.has_questions ? (
                <Alert
                  severity="info"
                  icon={false}
                  sx={{ width: '200px', textAlign: 'center' }}
                >
                  No application questions
                </Alert>
              ) : Number(result.rank) <= 3 ? (
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ width: '200px', textAlign: 'center' }}
                >
                  This opportunity has low potential
                </Alert>
              ) : generationError ? (
                <>
                  <Alert
                    severity="error"
                    sx={{ width: '200px', textAlign: 'center', my: -2 }}
                  >
                    Failed to generate
                  </Alert>
                  <Typography
                    variant="caption"
                    sx={{ mt: 2, mb: -3, color: '#666666' }}
                  >
                    Please try again later
                  </Typography>
                </>
              ) : applicationId ? (
                <Link to={`/applications/${result.need_id}/${applicationId}`}>
                  <Button
                    variant="contained"
                    disableRipple
                    sx={{
                      textTransform: 'none',
                      fontSize: '16px',
                      bgcolor: 'white',
                      color: '#1C3045',
                      border: 2,
                      borderColor: applicationBorderColor,
                      width: isMobile ? '100%' : '200px',
                      borderRadius: 2,
                      mt: isMobile ? 2 : 0,
                    }}
                  >
                    View Application
                  </Button>
                </Link>
              ) : (
                <Button
                  variant="contained"
                  disableRipple
                  onClick={() => handleGenerateApplication(result.id)}
                  disabled={
                    !!(
                      isGeneratingApplication ||
                      applicationId ||
                      applicationsLoading
                    )
                  }
                  sx={{
                    textTransform: 'none',
                    fontSize: '16px',
                    color: 'white',
                    bgcolor: '#1C3045',
                    width: isMobile ? '100%' : '200px',
                    borderRadius: 2,
                    mt: isMobile ? 2 : 0,
                    height: '40px',
                  }}
                >
                  {isGeneratingApplication ? (
                    <>
                      <Typography
                        sx={{
                          fontSize: '16px',
                          display: 'flex',
                          alignItems: 'center',
                          color: '#1C3045',
                          justifyContent: 'center',
                          width: '100%',
                          mt: 1,
                          mb: 0.5,
                          textAlign: 'center',
                        }}
                      >
                        <CircularProgress
                          size={14}
                          sx={{ color: '#1C3045', mr: 1.5 }}
                        />
                        {applicationProgress.message || 'Generating...'}
                      </Typography>
                    </>
                  ) : applicationsLoading ? (
                    <>
                      <CircularProgress
                        size={14}
                        sx={{ color: 'white', mr: 1.5 }}
                      />
                      Loading...
                    </>
                  ) : (
                    'Draft my application'
                  )}
                </Button>
              )}
              {isGeneratingApplication ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'right',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                    width: '100%',
                  }}
                >
                  <LinearProgress
                    variant="determinate"
                    value={applicationProgress.progress || 0}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      mt: 1,
                      mx: 1,
                      bgcolor: '#E0E0E0',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: '#1C3045',
                      },
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      width: '100%',
                      textAlign: 'right',
                      pr: 2,
                      mt: 0.5,
                      color: '#666',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      gap: 0.75,
                    }}
                  >
                    {applicationId ? (
                      <>
                        <CircularProgress size={12} sx={{ color: '#666' }} />
                        Regenerating... {applicationProgress.progress || 0}%
                      </>
                    ) : (
                      `${applicationProgress.progress || 0}% complete`
                    )}
                  </Typography>
                </Box>
              ) : null}
            </Box>

            {/* Negative feedback icon - always visible */}
            <AccessibleTooltip title="Remove match">
              <IconButton
                onClick={() => setShowNegativeFeedbackModal(true)}
                color="default"
                sx={{ ml: 1 }}
              >
                <ThumbDownIcon fontSize="small" />
              </IconButton>
            </AccessibleTooltip>
            {/* Postive feedback icon - always visible */}
            <AccessibleTooltip title="Liked match">
              <IconButton
                onClick={() => setShowPositiveFeedbackModal(true)}
                color="default"
                sx={{ ml: 1 }}
              >
                <ThumbUpIcon fontSize="small" />
              </IconButton>
            </AccessibleTooltip>
          </Box>
        </Box>
      </ListItem>

      {showResultDetails && (
        <Box
          id={`match-details-${result.id}`}
          sx={{ px: isMobile ? 1 : 4, py: isMobile ? 1 : 2 }}
        >
          {/* Opportunity status alert */}
          {displayInfo.status &&
            (isFuturePlanning && displayInfo.status === 'closed' ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Round dates not confirmed — this funder has run rounds
                  recently and may open again
                </Typography>
              </Alert>
            ) : displayInfo.message ? (
              <Alert
                severity={displayInfo.status === 'closed' ? 'error' : 'warning'}
                sx={{ mb: 2 }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {displayInfo.message}
                </Typography>
              </Alert>
            ) : null)}
          <Typography
            variant="body1"
            sx={{
              mt: isMobile ? 2 : 1,
              fontSize: '16px',
              color: '#1C3045',
            }}
          >
            {result.result}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              mt: 2,
              fontSize: '16px',
              color: '#1C3045',
            }}
          >
            <span style={{ fontWeight: '500' }}>Suitability:</span>{' '}
            {result.rank} / 5
          </Typography>
          <Typography
            variant="body1"
            sx={{
              mt: 2,
              fontSize: '16px',
              color: '#1C3045',
              fontWeight: '500',
            }}
          >
            {displayInfo.status === 'closed'
              ? 'Previous Application Dates:'
              : 'Application Dates:'}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              mt: 1,
              fontSize: '16px',
              color: '#1C3045',
            }}
          >
            {result.opportunity.always_open
              ? 'Always open'
              : result.application_open_close_dates
                  .filter(
                    // filter duplicates
                    (date, index, self) =>
                      index ===
                      self.findIndex(
                        (d) =>
                          d.application_open === date.application_open &&
                          d.application_close === date.application_close,
                      ),
                  )
                  .filter((date) => {
                    // for closed opportunities show all dates; otherwise filter out past dates
                    if (displayInfo.status === 'closed') return true
                    if (!date.application_close) return true

                    const today = new Date()
                    today.setHours(0, 0, 0, 0)
                    const closeDate = new Date(date.application_close)
                    closeDate.setHours(0, 0, 0, 0)

                    return closeDate >= today
                  })
                  .sort((a, b) => {
                    // sort in order of open date
                    if (!a.application_open || !b.application_open) return 0

                    return (
                      new Date(a.application_open).getTime() -
                      new Date(b.application_open).getTime()
                    )
                  })
                  .map((date, index) => {
                    // format open and close dates li
                    const openDate =
                      date.application_open && new Date(date.application_open)
                    const closeDate =
                      date.application_close && new Date(date.application_close)

                    const formattedOpenDate =
                      openDate instanceof Date
                        ? openDate.toLocaleDateString('en-NZ', {
                            month: 'long',
                            day: '2-digit',
                            year: 'numeric',
                          })
                        : 'Not provided'
                    const formattedCloseDate =
                      closeDate instanceof Date
                        ? closeDate.toLocaleDateString('en-NZ', {
                            month: 'long',
                            day: '2-digit',
                            year: 'numeric',
                          })
                        : 'Not provided'

                    const isEstimate = date.estimate_date_flag || false

                    return (
                      <>
                        {index === 0 && (
                          <Divider
                            key={'divider-top-' + index}
                            sx={{ mt: 1, mb: 1, maxWidth: '250px' }}
                          />
                        )}
                        <li key={'open-date-' + index}>
                          <span
                            style={{
                              fontWeight: '500',
                              marginRight: 20,
                              marginLeft: 5,
                            }}
                          >
                            Open{' '}
                          </span>{' '}
                          {formattedOpenDate}
                        </li>
                        <li key={'close-date-' + index}>
                          <span
                            style={{
                              fontWeight: '500',
                              marginRight: 19.5,
                              marginLeft: 5,
                            }}
                          >
                            Close
                          </span>{' '}
                          {formattedCloseDate}
                        </li>

                        {isEstimate && (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              my: 0.5,
                              gap: 0.5,
                              ml: 1,
                            }}
                            key={'is-estimate-' + index}
                          >
                            <AccessibleTooltip title="These round dates are estimated. They will be confirmed when more information is available.">
                              <span
                                role="img"
                                aria-label="Info: These round dates are estimated. They will be confirmed when more information is available."
                                tabIndex={0}
                              >
                                <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                              </span>
                            </AccessibleTooltip>
                            <Typography variant="body2" fontStyle="italic">
                              Estimate Only
                            </Typography>
                          </Box>
                        )}

                        <Divider
                          key={'divider-bottom-' + index}
                          sx={{ mt: 1, mb: 1, maxWidth: '250px' }}
                        />
                      </>
                    )
                  })}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              mt: 3,
              fontSize: '16px',
              color: '#1C3045',
              fontWeight: '500',
            }}
          >
            Points you may want to address in your application:
          </Typography>
          {result.questions &&
            result.questions.split('?').map(
              (question, index) =>
                question.trim() !== '' && (
                  <Typography key={index} variant="body1" sx={{ mt: 1 }}>
                    {/^\d+\./.test(question.trim())
                      ? question.trim()
                      : index + 1 + '. ' + question.trim()}
                    ?
                  </Typography>
                ),
            )}
        </Box>
      )}
      {showOppDetails && (
        <Box
          id={`funder-details-${result.id}`}
          sx={{
            px: isMobile ? 1 : 4,
            pt: isMobile ? 1 : 0.5,
            pb: isMobile ? 1 : 5,
          }}
        >
          {/* Opportunity status alert */}
          {displayInfo.status &&
            (isFuturePlanning && displayInfo.status === 'closed' ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Upcoming Funding Round dates not confirmed — this funder has
                  run rounds recently and may open again
                </Typography>
              </Alert>
            ) : displayInfo.message ? (
              <Alert
                severity={displayInfo.status === 'closed' ? 'error' : 'warning'}
                sx={{ mb: 2 }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {displayInfo.message}
                </Typography>
              </Alert>
            ) : null)}
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  mt: 2,
                  fontSize: '16px',
                  color: '#1C3045',
                }}
              >
                <span style={{ fontWeight: '500' }}>Funder:</span>{' '}
                {result.funder.name}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  mt: isMobile ? 2 : 0.5,
                  fontSize: '16px',
                  color: '#1C3045',
                }}
              >
                {(() => {
                  const link =
                    result.opportunity.opportunity_link || result.funder.website
                  const label = result.opportunity.opportunity_link
                    ? 'View Opportunity:'
                    : 'Website:'

                  if (link) {
                    return (
                      <>
                        <span style={{ fontWeight: '500' }}>{label}</span>{' '}
                        <a
                          href={link}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#1C3045' }}
                        >
                          {link}
                        </a>
                      </>
                    )
                  }

                  return (
                    <>
                      <span style={{ fontWeight: '500' }}>Website:</span> None
                      Provided
                    </>
                  )
                })()}
              </Typography>
              {result.opportunity.general_info && (
                <>
                  <Typography
                    variant="body1"
                    sx={{
                      mt: isMobile ? 2 : 0.5,
                      fontSize: '16px',
                      color: '#1C3045',
                      fontWeight: '500',
                    }}
                  >
                    Useful Info:
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      mt: 1,
                      color: '#1C3045',
                    }}
                  >
                    {renderTextWithLinks(result.opportunity.general_info)}
                  </Typography>
                </>
              )}
            </Box>
          </Box>
          <Typography
            variant="body1"
            sx={{
              mt: isMobile ? 2 : 3,
              fontSize: '20px',
              color: '#1C3045',
              fontWeight: '500',
            }}
          >
            Opportunity Details
          </Typography>

          <Typography
            variant="body1"
            sx={{
              fontSize: '16px',
              color: '#1C3045',
              '& ol': {
                mt: 1,
              },
              '& ul': {
                mt: 1,
              },
            }}
          >
            <ReactMarkdown
              components={{
                // Header components
                h1: ({ children }) => (
                  <Typography variant="h6" sx={{ mt: 1, mb: 0.5 }}>
                    {children}
                  </Typography>
                ),
                h2: ({ children }) => (
                  <Typography variant="h6" sx={{ mt: 1, mb: 0.5 }}>
                    {children}
                  </Typography>
                ),
                h3: ({ children }) => (
                  <Typography variant="h6" sx={{ mt: 1, mb: 0.5 }}>
                    {children}
                  </Typography>
                ),
                h4: ({ children }) => (
                  <Typography variant="h6" sx={{ mt: 1, mb: 0.5 }}>
                    {children}
                  </Typography>
                ),
                h5: ({ children }) => (
                  <Typography variant="h6" sx={{ mt: 1, mb: 0.5 }}>
                    {children}
                  </Typography>
                ),
                h6: ({ children }) => (
                  <Typography variant="h6" sx={{ mt: 1, mb: 0.5 }}>
                    {children}
                  </Typography>
                ),

                // Basic paragraph style (for non-list paragraphs)
                p: ({ children }) => (
                  <Typography
                    variant="body1"
                    sx={{
                      my: 3,
                      mb: 0,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {children}
                  </Typography>
                ),

                ul: ({ children, ...props }) => <ul {...props}>{children}</ul>,
                ol: ({ children, ...props }) => <ol {...props}>{children}</ol>,
                li: ({ children }) => (
                  <Typography
                    variant="body1"
                    component="li"
                    sx={{
                      marginBottom: '0.5rem',
                      lineHeight: '1.5',
                      padding: '0.1rem 0',
                      display: 'list-item',
                    }}
                  >
                    {children}
                  </Typography>
                ),

                strong: ({ children }) => (
                  <Typography
                    variant="body1"
                    style={{
                      fontWeight: 500,
                    }}
                  >
                    {children}
                  </Typography>
                ),
                em: ({ children }) => (
                  <em style={{ display: 'inline', fontStyle: 'italic' }}>
                    {children}
                  </em>
                ),
                a: ({ node, ...props }) => (
                  <a
                    {...props}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'inherit', textDecoration: 'underline' }}
                  />
                ),
              }}
            >
              {convertUrlsToMarkdown(
                result.opportunity.profile_text
                  .replace('Funder Profile: ', '')
                  .replace('Funder Profile ', '')
                  .replace('Funder Profile:', '')
                  .replace('Funder Profile', '')
                  .replace(/ *\*\*:/g, ':**'),
              )}
            </ReactMarkdown>
          </Typography>
        </Box>
      )}

      <Divider />
      {/* Negative Feedback Modal */}
      <FeedbackNegativeModal
        isOpen={showNegativeFeedbackModal}
        onClose={() => setShowNegativeFeedbackModal(false)}
        scanResultId={result.id}
        orgUuid={result.org_uuid}
        needId={result.need_id}
        onFeedbackSubmitted={handleFeedbackSubmitted}
        token={token as string}
      />
      {/* Postive Feedback Modal */}
      <FeedbackPostiveModal
        isOpen={showPositiveFeedbackModal}
        onClose={() => setShowPositiveFeedbackModal(false)}
        scanResultId={result.id}
        orgUuid={result.org_uuid}
        onFeedbackSubmitted={handleFeedbackSubmitted}
        token={token as string}
      />
    </>
  )
}
